import { useState, useEffect } from 'react';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { Box, Button, Chip, TextField } from '@mui/material';
import { Link } from 'react-router-dom';
import {
  PageHeader,
  SectionCard,
  LoadingState,
} from '@/components/shared';
import { SQLTargets } from '@/services/ceoxSettings';
import { OrgStack, STACK_CATEGORIES, PROVIDERS } from '@/types/api/ceoxStack';
import { Paths } from '@/types/routes';

type Props = {
  sqlTargets: SQLTargets | null;
  isSaving: boolean;
  onSave: (targets: SQLTargets) => void;
  stack: OrgStack | null;
};

const CHANNEL_LABELS: { key: keyof SQLTargets; label: string; description: string }[] = [
  { key: 'seo', label: 'SEO (Organic)', description: 'Monthly target for organic search SQLs (from GA4 conversions)' },
  { key: 'google_ads', label: 'Google Ads', description: 'Monthly target for paid search SQLs (from Google Ads conversions)' },
  { key: 'cold_email', label: 'Cold Email', description: 'Monthly target for cold outreach SQLs (from Smartlead replies)' },
  { key: 'pipeline', label: 'Pipeline (Direct)', description: 'Monthly target for direct/inbound SQLs (from Pipedrive new deals)' },
];

const CEOxSettingsComponent = function ({ sqlTargets, isSaving, onSave, stack }: Props): JSX.Element {
  const [draft, setDraft] = useState<SQLTargets>({
    seo: 20,
    google_ads: 50,
    cold_email: 30,
    pipeline: 40,
  });

  useEffect(() => {
    if (sqlTargets) setDraft(sqlTargets);
  }, [sqlTargets]);

  const handleChange = (key: keyof SQLTargets, value: string): void => {
    const num = parseInt(value, 10);
    if (!Number.isNaN(num) && num >= 0) {
      setDraft((prev) => ({ ...prev, [key]: num }));
    }
  };

  if (!sqlTargets) return <LoadingState message="Loading settings..." />;

  const totalTarget = CHANNEL_LABELS.reduce((s, ch) => s + draft[ch.key], 0);

  return (
    <>
      <PageHeader
        title="CEOx Settings"
        actions={
          <Button
            variant="contained"
            size="small"
            onClick={() => onSave(draft)}
            disabled={isSaving}
          >
            <SaveOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        }
      />

      {stack && (
        <SectionCard
          title="Business Stack"
          subtitle="The tools your organization uses — these determine what shows on your CEOx dashboard"
          action={
            <Button
              component={Link}
              to={Paths.CEOX_STACK_SETUP}
              variant="outlined"
              size="small"
              sx={{ textTransform: 'none', borderRadius: '999px' }}
            >
              <EditOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Edit Stack
            </Button>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {STACK_CATEGORIES.map((cat) => {
              const entry = stack[cat.key];
              const provider = entry
                ? PROVIDERS[cat.key].find((p) => p.id === entry.provider)
                : null;
              const isNone = !entry || entry.provider === 'none';
              return (
                <Box
                  key={cat.key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 140 }}>
                    <Box sx={{ fontSize: 14, fontWeight: 500, color: '#09090B' }}>{cat.label}</Box>
                  </Box>
                  <Chip
                    label={isNone ? 'Not configured' : provider?.label ?? entry?.provider}
                    size="small"
                    sx={{
                      borderRadius: '4px',
                      fontWeight: 500,
                      fontSize: 12,
                      backgroundColor: isNone ? '#F4F4F5' : '#F0FDF4',
                      color: isNone ? '#71717A' : '#16A34A',
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        </SectionCard>
      )}

      <Box sx={{ mb: 3 }} />

      <SectionCard
        title="Monthly SQL Targets"
        subtitle={`Set your monthly Sales Qualified Lead targets per channel \u2014 total: ${totalTarget} SQLs/month`}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {CHANNEL_LABELS.map((ch) => (
            <Box
              key={ch.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Box sx={{ fontSize: 14, fontWeight: 500, color: '#09090B' }}>{ch.label}</Box>
                <Box sx={{ fontSize: 12, color: '#71717A', mt: 0.25 }}>{ch.description}</Box>
              </Box>
              <TextField
                type="number"
                size="small"
                value={draft[ch.key]}
                onChange={(e) => handleChange(ch.key, e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                sx={{
                  width: 120,
                  '& .MuiInputBase-input': { fontSize: 14, textAlign: 'right' },
                }}
              />
              <Box sx={{ fontSize: 12, color: '#71717A', minWidth: 60 }}>SQLs / month</Box>
            </Box>
          ))}
        </Box>
      </SectionCard>

      <Box sx={{ mb: 3 }} />

      <SectionCard
        title="How SQL Tracking Works"
        subtitle="What each channel measures and what you need to set up"
      >
        <Box
          sx={{
            '& table': { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
            '& th': {
              textAlign: 'left',
              padding: '8px 12px',
              color: '#71717A',
              fontWeight: 500,
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              borderBottom: '2px solid #E4E4E7',
            },
            '& td': {
              padding: '10px 12px',
              borderBottom: '1px solid #F4F4F5',
              fontSize: 13,
              verticalAlign: 'top',
            },
            '& tbody tr': {
              transition: 'background-color 200ms cubic-bezier(0.2, 0.8, 0.4, 1)',
              '&:hover': { backgroundColor: '#FAFAFA' },
            },
          }}
        >
          <table>
            <thead>
              <tr>
                <th>Channel</th>
                <th>What Counts as SQL</th>
                <th>Data Source</th>
                <th>Setup Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ fontWeight: 500 }}>SEO (Organic)</td>
                <td>GA4 conversions from organic search traffic</td>
                <td>Google Analytics 4 + Search Console</td>
                <td>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    <li>Connect Google account in Integrations</li>
                    <li>Set up GA4 conversion events (form submissions)</li>
                    <li>Select GA4 property in GrowthX Settings</li>
                  </Box>
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>Google Ads</td>
                <td>Google Ads conversion actions (form fills, calls)</td>
                <td>Google Ads API</td>
                <td>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    <li>Connect Google Ads in Integrations</li>
                    <li>Set up conversion tracking in Google Ads</li>
                    <li>Import GA4 conversions into Google Ads</li>
                  </Box>
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>Cold Email</td>
                <td>Smartlead replies (warm responses)</td>
                <td>Smartlead API</td>
                <td>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    <li>Connect Smartlead in Integrations</li>
                    <li>Tag positive replies vs auto-replies (future)</li>
                  </Box>
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>Pipeline (Direct)</td>
                <td>New Pipedrive deals created this month</td>
                <td>Pipedrive API</td>
                <td>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    <li>Connect Pipedrive in Integrations</li>
                    <li>Add <code>lead_source</code> custom field to deals</li>
                    <li>Define which stage = &quot;SQL&quot; in your pipeline</li>
                  </Box>
                </td>
              </tr>
            </tbody>
          </table>
        </Box>
      </SectionCard>

      <Box sx={{ mb: 3 }} />

      <SectionCard
        title="Future: True SQL Tracking via Pipedrive"
        subtitle="What your team needs to do for accurate per-channel SQL data"
      >
        <Box sx={{ fontSize: 13, color: '#4A4B4E', lineHeight: 1.8 }}>
          <Box sx={{ fontWeight: 500, color: '#09090B', mb: 1 }}>
            To get real Won/Lost data per channel, your team needs to:
          </Box>
          <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
            <li>
              <strong>Create a &quot;Lead Source&quot; custom field in Pipedrive</strong> with values:{' '}
              <code>google_ads</code>, <code>cold_email</code>, <code>organic</code>, <code>direct</code>, <code>referral</code>
            </li>
            <li>
              <strong>Set up UTM tracking</strong> on Google Ads landing pages and website forms so the source
              is captured automatically when a deal is created
            </li>
            <li>
              <strong>Connect Smartlead to Pipedrive</strong> so cold email replies auto-create deals
              tagged with <code>cold_email</code> source
            </li>
            <li>
              <strong>Define an &quot;SQL&quot; stage</strong> in your Pipedrive pipeline (e.g., &quot;Qualified&quot; or
              &quot;Discovery Call Booked&quot;) — deals reaching this stage count as SQLs
            </li>
          </Box>
          <Box sx={{ mt: 2, p: 2, borderRadius: '8px', backgroundColor: '#F0F9FF', border: '1px solid #BAE6FD' }}>
            <Box sx={{ fontSize: 12, fontWeight: 500, color: '#0369A1' }}>
              Once this is set up, the dashboard will automatically show SQLs, Won, and Lost per channel
              by filtering Pipedrive deals by source and stage.
            </Box>
          </Box>
        </Box>
      </SectionCard>
    </>
  );
};

export default CEOxSettingsComponent;
